import { useState, useEffect } from 'react'
import {
  Typography,
  Stack,
  Container,
  Card,
  CardContent,
  Box,
  Grid,
  Button,
  Table,
  Sheet,
  Chip,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
  Textarea,
  Divider,
  Select,
  Option,
  Alert,
} from '@mui/joy'
import {
  Label as TagIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import api from '../services/api'

interface Tag {
  id: number
  name: string
  color: string
  uses?: number
  createdAt: string
  description?: string
  // Auto-etiquetado por palabras clave (solo etiquetas normales, kanban = 0)
  autoTagEnabled?: boolean
  autoTagKeywords?: string
  autoTagMatchMode?: string
  autoTagExcludeKeywords?: string
  autoTagScope?: string
  // Aviso a grupo de WhatsApp al aplicarse la etiqueta
  alertEnabled?: boolean
  alertGroupTicketId?: number | null
  alertMessage?: string
}

/**
 * Grupo disponible como destino de aviso.
 * `ticketId` identifica grupo + conexión: un mismo grupo puede existir en
 * varias conexiones y el aviso sale por la del ticket elegido.
 */
interface AlertGroup {
  ticketId: number
  groupName: string
  connectionId: number | null
  connectionName: string
  channel: string
  status: string
  available: boolean
  reason?: string
}

const FORM_INICIAL = {
  name: '',
  color: '#3b82f6',
  kanban: 0,
  description: '',
  autoTagEnabled: false,
  autoTagKeywords: '',
  autoTagMatchMode: 'word',
  autoTagExcludeKeywords: '',
  autoTagScope: 'inbound',
  alertEnabled: false,
  alertGroupTicketId: null as number | null,
  alertMessage: '',
}

export default function Tags() {
  const [tags, setTags] = useState<Tag[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [openModal, setOpenModal] = useState(false)
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null)
  const [formData, setFormData] = useState(FORM_INICIAL)
  const [alertGroups, setAlertGroups] = useState<AlertGroup[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [generandoPlantilla, setGenerandoPlantilla] = useState(false)
  // Sin esto, un fallo al guardar solo salía por console.error y el usuario
  // veía el modal quieto sin ninguna explicación.
  const [errorGuardado, setErrorGuardado] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)

  /** Extrae el mensaje real del backend en vez de un "Error" genérico. */
  const mensajeDeError = (error: any): string => {
    const status = error?.response?.status
    const backend = error?.response?.data?.error || error?.response?.data?.message

    if (status === 403) {
      return 'No tienes permiso: el auto-etiquetado y el aviso a grupo solo puede configurarlos un administrador.'
    }
    if (backend) return String(backend)
    if (status) return `El servidor respondió ${status}.`
    return error?.message || 'No se pudo guardar. Revisa tu conexión.'
  }

  useEffect(() => {
    fetchTags()
    fetchAlertGroups()
  }, [])

  const fetchAlertGroups = async () => {
    try {
      setLoadingGroups(true)
      const { data } = await api.get('/tags/alert-groups')
      setAlertGroups(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching alert groups:', error)
      setAlertGroups([])
    } finally {
      setLoadingGroups(false)
    }
  }

  /**
   * Genera la plantilla con IA. Se llama una sola vez por configuración:
   * en cada aviso el backend solo sustituye las variables, sin coste.
   */
  const generarPlantillaIA = async () => {
    if (!formData.name.trim()) return
    try {
      setGenerandoPlantilla(true)
      const { data } = await api.post('/tags/alert-message/ai-recommend', {
        name: formData.name,
        description: formData.description,
      })
      if (data?.message) {
        setFormData({ ...formData, alertMessage: data.message })
      }
    } catch (error) {
      console.error('Error generating alert template:', error)
    } finally {
      setGenerandoPlantilla(false)
    }
  }

  const fetchTags = async () => {
    try {
      setLoading(true)
      const response = await api.get('/tags')
      // console.log('Tags API response:', response.data)
      void 0;

      // Backend returns { tags, count, hasMore }
      const tagsData = Array.isArray(response.data)
        ? response.data
        : (response.data.tags || [])

      setTags(tagsData)

      if (tagsData.length === 0) {
        // console.log('No tags found in database')
        void 0;
      }
    } catch (error) {
      console.error('Error fetching tags:', error)
      // Show empty array on error to see real state
      setTags([])
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    try {
      setGuardando(true)
      setErrorGuardado(null)
      await api.post('/tags', formData)
      await fetchTags()
      setOpenModal(false)
      resetForm()
    } catch (error) {
      console.error('Error creating tag:', error)
      setErrorGuardado(mensajeDeError(error))
    } finally {
      setGuardando(false)
    }
  }

  const handleUpdate = async () => {
    if (!selectedTag) return
    try {
      setGuardando(true)
      setErrorGuardado(null)
      await api.put(`/tags/${selectedTag.id}`, formData)
      await fetchTags()
      setOpenModal(false)
      resetForm()
    } catch (error) {
      console.error('Error updating tag:', error)
      setErrorGuardado(mensajeDeError(error))
    } finally {
      setGuardando(false)
    }
  }

  const handleDelete = async (tagId: number) => {
    if (confirm('¿Estás seguro de eliminar esta etiqueta?')) {
      try {
        await api.delete(`/tags/${tagId}`)
        fetchTags()
      } catch (error) {
        console.error('Error deleting tag:', error)
      }
    }
  }

  const openEditModal = (tag: Tag) => {
    setSelectedTag(tag)
    setErrorGuardado(null)
    setFormData({
      name: tag.name,
      color: tag.color,
      kanban: 0,
      description: tag.description || '',
      autoTagEnabled: Boolean(tag.autoTagEnabled),
      autoTagKeywords: tag.autoTagKeywords || '',
      autoTagMatchMode: tag.autoTagMatchMode || 'word',
      autoTagExcludeKeywords: tag.autoTagExcludeKeywords || '',
      autoTagScope: tag.autoTagScope || 'inbound',
      alertEnabled: Boolean(tag.alertEnabled),
      alertGroupTicketId: tag.alertGroupTicketId ?? null,
      alertMessage: tag.alertMessage || '',
    })
    setOpenModal(true)
  }

  const openCreateModal = () => {
    setSelectedTag(null)
    setErrorGuardado(null)
    resetForm()
    setOpenModal(true)
  }

  const resetForm = () => {
    setFormData(FORM_INICIAL)
  }

  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const stats = {
    total: tags.length,
    used: tags.reduce((sum, tag) => sum + (tag.uses || 0), 0),
    mostUsed: tags.sort((a, b) => (b.uses || 0) - (a.uses || 0))[0]?.name || '-',
  }

  return (
    <Container maxWidth="xl">
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <TagIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography level="h2">Etiquetas</Typography>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Gestión de etiquetas para clasificación
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <IconButton variant="outlined" color="neutral" onClick={fetchTags}>
              <RefreshIcon />
            </IconButton>
            <Button startDecorator={<AddIcon />} color="primary" onClick={openCreateModal}>
              Nueva Etiqueta
            </Button>
          </Stack>
        </Stack>

        {/* Stats */}
        <Grid container spacing={2}>
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Total Etiquetas
                </Typography>
                <Typography level="h2">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Más Usada
                </Typography>
                <Typography level="body-sm" fontWeight="bold">
                  {stats.mostUsed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          {/* Card "Usos Totales" COMENTADA (2026-07-07): el valor NO era confiable.
              Causas: (1) stats.used suma tag.uses de solo la PÁGINA cargada (GET /tags
              devuelve limit=10, no todas las etiquetas); (2) tag.uses llega como string
              (COUNT(*) de Postgres = bigint), así que el reduce con "+" concatenaba en vez
              de sumar. Para reactivarla con un total real, exponer un agregado (SUM global
              de ContactTags por company) en el backend y usar ese campo aquí.
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Usos Totales
                </Typography>
                <Typography level="h2">{stats.used}</Typography>
              </CardContent>
            </Card>
          </Grid>
          */}
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Activas
                </Typography>
                <Typography level="h2" sx={{ color: 'success.main' }}>
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search */}
        <Card>
          <CardContent>
            <Input
              placeholder="Buscar etiquetas..."
              startDecorator={<SearchIcon />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </CardContent>
        </Card>

        {/* Tags Table */}
        <Card>
          <Sheet sx={{ overflow: 'auto' }}>
            <Table stickyHeader>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Color</th>
                  <th style={{ width: 200 }}>Etiqueta</th>
                  <th style={{ width: 100 }}>Usos</th>
                  <th style={{ width: 180 }}>Fecha Creación</th>
                  <th style={{ width: 150 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                      <Typography>Cargando etiquetas...</Typography>
                    </td>
                  </tr>
                ) : filteredTags.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                      <Typography>No se encontraron etiquetas</Typography>
                    </td>
                  </tr>
                ) : (
                  filteredTags.map((tag) => (
                    <tr key={tag.id}>
                      <td>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            bgcolor: tag.color,
                            borderRadius: '50%',
                            border: '2px solid',
                            borderColor: 'background.surface',
                            boxShadow: `0 0 0 1px ${tag.color}`,
                            flexShrink: 0,
                          }}
                        />
                      </td>
                      <td>
                        <Chip size="sm" sx={{ bgcolor: tag.color, color: 'white' }}>
                          {tag.name}
                        </Chip>
                      </td>
                      <td>
                        <Typography level="body-sm">{tag.uses || 0}</Typography>
                      </td>
                      <td>
                        <Typography level="body-xs">
                          {new Date(tag.createdAt).toLocaleDateString('es-ES')}
                        </Typography>
                      </td>
                      <td>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="primary"
                            onClick={() => openEditModal(tag)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => handleDelete(tag.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Sheet>
        </Card>

        {/* Modal Create/Edit */}
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <ModalDialog sx={{ minWidth: { xs: 0, sm: 'min(500px, calc(100vw - 32px))' } }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              {selectedTag ? 'Editar Etiqueta' : 'Nueva Etiqueta'}
            </Typography>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Nombre</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: VIP"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Color</FormLabel>
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography level="body-sm">Vista Previa:</Typography>
                <Chip size="sm" sx={{ bgcolor: formData.color, color: 'white' }}>
                  {formData.name || 'Etiqueta'}
                </Chip>
              </Box>
              <FormControl>
                <FormLabel>Descripción</FormLabel>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción de la etiqueta (opcional)"
                />
              </FormControl>

              <Divider />

              {/* ── Auto-etiquetado por palabras clave ─────────────────────── */}
              <Box>
                <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                  <Box>
                    <FormLabel sx={{ mb: 0.5 }}>Colocar automáticamente</FormLabel>
                    <FormHelperText sx={{ mt: 0 }}>
                      Se añade sola a la conversación cuando el cliente escribe
                      alguna de las palabras clave.
                    </FormHelperText>
                  </Box>
                  <Switch
                    checked={formData.autoTagEnabled}
                    onChange={(e) =>
                      setFormData({ ...formData, autoTagEnabled: e.target.checked })
                    }
                  />
                </FormControl>
              </Box>

              {formData.autoTagEnabled && (
                <Stack spacing={2}>
                  <FormControl>
                    <FormLabel>Palabras clave</FormLabel>
                    <Textarea
                      minRows={2}
                      value={formData.autoTagKeywords}
                      onChange={(e) =>
                        setFormData({ ...formData, autoTagKeywords: e.target.value })
                      }
                      placeholder="drenaje, drenaje linfatico, masaje reductor"
                    />
                    <FormHelperText>
                      Sepáralas con coma o punto y coma. No distingue mayúsculas
                      ni acentos.
                    </FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel>No colocar si el mensaje contiene</FormLabel>
                    <Textarea
                      minRows={2}
                      value={formData.autoTagExcludeKeywords}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          autoTagExcludeKeywords: e.target.value,
                        })
                      }
                      placeholder="no quiero, no me interesa, ya no"
                    />
                    <FormHelperText>
                      Evita colocarla en frases negativas como «no quiero
                      drenaje».
                    </FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Tipo de coincidencia</FormLabel>
                    <Select
                      value={formData.autoTagMatchMode}
                      onChange={(_, v) =>
                        setFormData({ ...formData, autoTagMatchMode: v || 'word' })
                      }
                    >
                      <Option value="word">
                        Palabra completa (recomendado)
                      </Option>
                      <Option value="substring">
                        Parte de una palabra (más agresivo)
                      </Option>
                    </Select>
                    <FormHelperText>
                      «Palabra completa» evita que «cera» se active dentro de
                      «cerabruta».
                    </FormHelperText>
                  </FormControl>

                  <Alert color="warning" variant="soft" size="sm">
                    La etiqueta solo se <b>coloca</b>, nunca se quita sola. Si se
                    pone por error, quítala desde la conversación y no volverá a
                    colocarse.
                  </Alert>
                </Stack>
              )}

              <Divider />

              {/* ── Aviso a grupo de WhatsApp ───────────────────────────── */}
              <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                <Box>
                  <FormLabel sx={{ mb: 0.5 }}>Avisar a un grupo</FormLabel>
                  <FormHelperText sx={{ mt: 0 }}>
                    Manda un mensaje a un grupo interno cuando esta etiqueta se
                    coloque automáticamente.
                  </FormHelperText>
                </Box>
                <Switch
                  checked={formData.alertEnabled}
                  onChange={(e) =>
                    setFormData({ ...formData, alertEnabled: e.target.checked })
                  }
                />
              </FormControl>

              {formData.alertEnabled && (
                <Stack spacing={2} sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'primary.300' }}>
                  {loadingGroups ? (
                    <Alert color="neutral" variant="soft" size="sm">
                      Buscando grupos disponibles…
                    </Alert>
                  ) : alertGroups.filter((g) => g.available).length === 0 ? (
                    /* Card explicativa: un select vacío sin motivo desconcierta */
                    <Alert color="warning" variant="soft">
                      <Box>
                        <Typography level="title-sm">
                          No hay grupos disponibles para avisar
                        </Typography>
                        <Typography level="body-sm" sx={{ mt: 0.5 }}>
                          Para usar esta función, una de tus conexiones de
                          WhatsApp debe pertenecer a un grupo y haber recibido al
                          menos un mensaje en él (así ChatEAM lo registra).
                        </Typography>
                        {alertGroups.length > 0 && (
                          <Typography level="body-xs" sx={{ mt: 1 }}>
                            Detectados {alertGroups.length} grupo(s), pero ninguno
                            utilizable:{' '}
                            {Array.from(
                              new Set(alertGroups.map((g) => g.reason).filter(Boolean))
                            ).join(' · ')}
                          </Typography>
                        )}
                      </Box>
                    </Alert>
                  ) : (
                    <FormControl>
                      <FormLabel>Grupo destino</FormLabel>
                      <Select
                        placeholder="Elige el grupo…"
                        value={formData.alertGroupTicketId}
                        onChange={(_, v) =>
                          setFormData({
                            ...formData,
                            alertGroupTicketId: (v as number) ?? null,
                          })
                        }
                      >
                        {alertGroups.map((g) => (
                          <Option
                            key={g.ticketId}
                            value={g.ticketId}
                            disabled={!g.available}
                          >
                            {g.groupName} — {g.connectionName}
                            {!g.available ? ` (${g.reason})` : ''}
                          </Option>
                        ))}
                      </Select>
                      <FormHelperText>
                        El aviso se envía por la conexión del grupo, que puede
                        ser distinta de la del ticket.
                      </FormHelperText>
                    </FormControl>
                  )}

                  <FormControl>
                    <Stack
                      direction="row"
                      sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}
                    >
                      <FormLabel sx={{ mb: 0 }}>Mensaje del aviso</FormLabel>
                      <Button
                        size="sm"
                        variant="soft"
                        loading={generandoPlantilla}
                        disabled={!formData.name.trim()}
                        onClick={generarPlantillaIA}
                      >
                        Redactar con IA
                      </Button>
                    </Stack>
                    <Textarea
                      minRows={4}
                      placeholder="🔔 Ticket etiquetado como {{etiqueta}}…"
                      value={formData.alertMessage}
                      onChange={(e) =>
                        setFormData({ ...formData, alertMessage: e.target.value })
                      }
                    />
                    <FormHelperText>
                      Variables: <code>{'{{contacto}}'}</code>{' '}
                      <code>{'{{telefono}}'}</code> <code>{'{{etiqueta}}'}</code>{' '}
                      <code>{'{{conexion_ticket}}'}</code>{' '}
                      <code>{'{{fecha}}'}</code> <code>{'{{link}}'}</code>
                    </FormHelperText>
                  </FormControl>

                  <Alert color="primary" variant="soft" size="sm">
                    El aviso se manda <b>una sola vez</b>, cuando la etiqueta se
                    coloca por primera vez. Los mensajes siguientes del cliente no
                    lo repiten.
                  </Alert>
                </Stack>
              )}

              {errorGuardado && (
                <Alert color="danger" variant="soft">
                  <Box>
                    <Typography level="title-sm">No se pudo guardar</Typography>
                    <Typography level="body-sm">{errorGuardado}</Typography>
                  </Box>
                </Alert>
              )}

              <Button
                color="primary"
                loading={guardando}
                onClick={selectedTag ? handleUpdate : handleCreate}
              >
                {selectedTag ? 'Actualizar' : 'Crear'} Etiqueta
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>
      </Stack>
    </Container>
  )
}
